import React from 'react';

// Single source of truth for the per-point pipeline stage indicator: a compact
// icon + ordinal-number badge on each point row, plus the legend above the list.
// The stage is DERIVED from the point object (no new DB field) — status,
// task_loop, critic_loop, task_choice. Colour encodes the coarse state:
//   grey  — not started        blue  — an agent is generating right now
//   amber — waiting on a human  green — approved
export const STAGE_COLORS = {
  pending: '#9aa0a6',
  running: '#1a73e8',
  waiting: '#e37400',
  done: '#188038',
};

// The five per-point stages, in order. Numbers are what the badge shows.
export const STAGES = [
  { n: 1, icon: '🔬', label: 'Researcher' },
  { n: 2, icon: '🃏', label: 'Card' },
  { n: 3, icon: '✍️', label: 'Draft' },
  { n: 4, icon: '⚖️', label: 'Critic' },
  { n: 5, icon: '🧩', label: 'Formalize' },
];

// Map a point to { n, icon, label, color, state, round }. `n` is null only for
// the terminal ✅. Kept deliberately explicit — the checks are ordered so the
// first match wins.
export function deriveStage(point) {
  if (!point) return null;
  const status = point.status;
  const taskLoop = point.task_loop || null;
  const critic = point.critic_loop || null;
  const choice = point.task_choice || null;
  const round = (critic && critic.rounds_done) || null;
  const at = (i, state, extra) => ({ ...STAGES[i], color: STAGE_COLORS[state], state, ...extra });

  // Terminal — approved / verified. A successful critic loop sets this itself.
  if (status === 'approved' || status === 'verified') {
    return { n: null, icon: '✅', label: 'Approved', color: STAGE_COLORS.done, state: 'done' };
  }
  // Something is generating right now → blue, named by which loop.
  if (critic && critic.status === 'running') {
    return at((critic.rounds_done || 0) < 1 ? 2 : 3, 'running', { round }); // first pass = Draft ✍️3, later = Critic ⚖️4
  }
  if (taskLoop && taskLoop.status === 'running') return at(0, 'running'); // Researcher 🔬1
  // A critic loop exists but settled without approval (max_rounds / interrupted
  // / error), or a draft carries a comment → it waits for the human at review.
  if (critic || status === 'commented' || status === 'draft') return at(3, 'waiting', { round }); // ⚖️4
  // A decision is made but the writer never ran → waiting on the human's Write.
  if (choice) return at(2, 'waiting'); // ✍️3
  // No decision yet — the human may still write/pick a card (research may be
  // done). Not owed: without a pick the point writes as no-task by default.
  if (taskLoop) return at(1, 'waiting'); // 🃏2
  // Nothing has started on this point.
  return at(0, 'pending'); // 🔬1
}

// The badge shown on a point row: icon + stage number, coloured by state.
export function StageBadge({ point, style }) {
  const s = deriveStage(point);
  if (!s) return null;
  const tip = `Stage ${s.n || '✓'} · ${s.label}`
    + (s.round ? ` · round ${s.round}` : '')
    + ` · ${s.state === 'running' ? 'generating now'
      : s.state === 'waiting' ? 'waiting on you'
      : s.state === 'done' ? 'approved' : 'not started'}`;
  return (
    <span
      title={tip}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 3,
        background: s.color, color: '#fff', borderRadius: 10,
        padding: '1px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
        ...style,
      }}
    >
      <span style={{ fontSize: 12 }}>{s.icon}</span>{s.n != null ? s.n : ''}
    </span>
  );
}

// One-line legend, shown above the list only while a run is open.
export function StageLegend() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 12px',
      padding: '4px 10px', margin: '0 0 8px', fontSize: 11.5, opacity: 0.7,
      borderBottom: '1px solid var(--border,#eee)',
    }}>
      <span style={{ fontWeight: 600, opacity: 0.8 }}>Pipeline stage:</span>
      {STAGES.map((s) => (
        <span key={s.n} style={{ whiteSpace: 'nowrap' }}>{s.icon} {s.n} {s.label}</span>
      ))}
      <span style={{ whiteSpace: 'nowrap' }}>✅ Approved</span>
    </div>
  );
}
